import React, { useEffect, useState } from 'react';
import { HlsVideo } from './HlsVideo';

const API_BASE = window.location.hostname.startsWith('shadow.')
  ? 'https://shadow.api.intrlock.io'
  : (process.env.REACT_APP_API_URL || 'https://api.intrlock.io');

function authHeaders(): HeadersInit {
  const t = localStorage.getItem('intrlock_token') || '';
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Shared modal for playing any Turing camera. Used by /cameras and /devices.
// Caller passes the row's envelope_token + property_id; overlay finds the
// enrolled Turing system for that property and fetches a stream URL.
export const TuringStreamOverlay: React.FC<{
  envelopeToken: string;
  propertyId: string;
  onClose: () => void;
}> = ({ envelopeToken, propertyId, onClose }) => {
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [systemId, setSystemId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const fetchStream = async (): Promise<string | null> => {
    try {
      const sr = await fetch(`${API_BASE}/integrations/turing/systems`, { headers: authHeaders() });
      if (!sr.ok) { setError('Failed to load Turing systems.'); return null; }
      const systems = await sr.json();
      const system = (systems || []).find((s: any) => String(s.entity_id) === String(propertyId));
      if (!system) { setError('No Turing system attached to this property.'); return null; }
      setSystemId(system.id);
      const r = await fetch(`${API_BASE}/integrations/turing/systems/${system.id}/stream-url`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ camera_id: envelopeToken, resolution: 'sub' }),
      });
      const data = await r.json();
      if (data.error === 'license_required') { setError(data.message || 'Camera does not have a Core AI license.'); return null; }
      if (data.play_url) { setStreamUrl(data.play_url); setError(null); return data.play_url; }
      setError('No stream URL returned.');
      return null;
    } catch (e: any) {
      setError(String(e?.message ?? e).slice(0, 200));
      return null;
    }
  };

  useEffect(() => { void fetchStream(); /* eslint-disable-next-line */ }, [envelopeToken, propertyId]);

  const refresh = async () => {
    if (!systemId) return null;
    try {
      const r = await fetch(`${API_BASE}/integrations/turing/systems/${systemId}/stream-url`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ camera_id: envelopeToken, resolution: 'sub' }),
      });
      const data = await r.json();
      return data.play_url || null;
    } catch { return null; }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative w-full max-w-3xl aspect-video bg-black rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {error ? (
          <div className="absolute inset-0 flex items-center justify-center text-white text-sm p-6 text-center">{error}</div>
        ) : streamUrl ? (
          <HlsVideo src={streamUrl} onRefreshStreamUrl={refresh} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white text-sm">Loading stream…</div>
        )}
        <button className="absolute top-2 right-2 bg-white/10 text-white rounded px-2 py-1 text-xs" onClick={onClose}>Close</button>
      </div>
    </div>
  );
};
